/* eslint-disable consistent-return */
import ComponentMetadata, { DEFAULT_SIZES } from '../ComponentDictionaryEntries/Entries';
import {
  ADOBE_TARGET,
  BENTO_HERO,
  COMPONENT_SELECTOR,
  INTERNAL_MARKETING_BANNER,
  QUICK_LINKS,
  SWEEPSTAKES_BANNER,
} from '../utils/Constants';

const getSizesMetadata = (type) => {
  return ComponentMetadata[`${type}Metadata`]?.sizes || DEFAULT_SIZES;
};

export const getComponent = (item, index, key) => {
  const {
    id, __typename, components, customRenderId
  } = item || {};
  const type = __typename || key;

  let componentProps = {
    ...item,
    id: `${id || index + type + 'Component'}`,
    __typename: type, // we keep this for now as a fallback, in case we don't have a render func for `customRenderId`
    customRenderId: customRenderId || type,
    sizes: getSizesMetadata(__typename),
  };

  if (!componentProps.__typename) return null;

  if (components && components.length > 0) {
    componentProps.children = components.map((component) => getComponent(component, index));
  }

  return componentProps;
};

export const getComponentsFromItems = (items, idx) => {
  let arrOfData = [];

  items.forEach((component, index) => {
    const {
      id, __typename, components, customRenderId
    } = component || {};

    let componentProps = {
      ...component,
      id: `${id || index + __typename + 'Component'}`,
      __typename,
      customRenderId: customRenderId || __typename,
      sizes: getSizesMetadata(__typename),
    };

    if (components && components.length > 0) {
      componentProps.children = components.map((comp) => getComponent(comp, idx));
    }

    arrOfData.push({
      ...componentProps,
    });
  });

  return arrOfData;
};

export const transformDataFromContentful = (arr = [], rawData) => {
  let arrOfData = arr;
  if (!rawData) return null;
  Object.entries(rawData).forEach(([key, value], idx) => {
    if (!value) return null;
    if (typeof value === 'string') {
      arrOfData.push(
        getComponent(
          {
            value,
            id: `${key}`,
            __typename: key,
          },
          idx,
          key,
        ),
      );
      return;
    }

    if (value?.components) {
      const val = getComponent(value, idx, key);
      if (val) {
        arrOfData.push(val);
      }
    } else if (Array.isArray(value)) {
      arrOfData.push(...getComponentsFromItems(value, idx));
    } else if (key === SWEEPSTAKES_BANNER
      || key === INTERNAL_MARKETING_BANNER
      || key === BENTO_HERO
      || key === QUICK_LINKS) {
      // to make internalMarketingBanner the actual customRenderId
      const newVal = { ...value, customRenderId: key };
      arrOfData.push(getComponent(newVal, idx, key));
    } else {
      transformDataFromContentful(arrOfData, value);
    }
  });
  return arrOfData;
};

// Helper function to extract image URL from various formats
const getImageUrl = (imageData) => {
  if (!imageData) return null;

  // Prioritize imageHotspot if it exists
  if (imageData?.imageHotspot?.imageUrl) {
    return imageData.imageHotspot.imageUrl;
  }

  // Fall back to DAM data
  return imageData?.damContentSelector?.assetData?.[0]?.selectedImageUrl
         || imageData?.damDownloadedContent?.url
         || null;
};

const getHeroImages = ({ heroCarouselData, isMobile }) => {
  const firstHero = heroCarouselData?.components?.[0]?.__typename === 'ComponentSelector'
    ? heroCarouselData?.components?.[0]?.defaultComponent?.content : heroCarouselData?.components?.[0];
  const heroImageId = isMobile ? 'preloadHeroSmallImage' : 'preloadHeroLargeImage';

  const previewImage = isMobile ? firstHero?.mobilePreviewImage : firstHero?.previewImage;
  let imageUrl = previewImage?.damContentSelector?.assetData?.[0]?.selectedImageUrl
                  || previewImage?.damDownloadedContent?.url;
  if (previewImage?.imageHotspot?.imageUrl) {
    imageUrl = previewImage?.imageHotspot?.imageUrl;
  }

  return [{
    preloadedImageUrl: imageUrl,
    preloadedImageId: heroImageId
  }];
};

/**
 * Checks if the component is a ComponentSelector and returns the appropriate content based on targeting and variations.
 *
 * @param {Object} componentToCheck This is either the original component or the ComponentSelector.
 * @param {boolean} renderDefault A boolean indicating whether to render the default component of the ComponentSelector.
 * @param {Object[]} variationIds An array of variation IDs for targeting for the ComponentSelector.
 * @returns {Object} The componentToCheck or if a ComponentSelector was passed in, the appropriate component content
 *   based on targeting and variations.
 */
const getComponentFromComponentSelector = (componentToCheck, renderDefault, variationIds) => {
  let bayComponent = componentToCheck;
  if (componentToCheck.__typename === COMPONENT_SELECTOR) {
    bayComponent = componentToCheck.defaultComponent?.content;
    const targetingType = componentToCheck.targetingType?.__typename;
    if (variationIds?.length > 0 && !renderDefault && targetingType === ADOBE_TARGET) {
      const targetId = componentToCheck.targetingType?.mbox;
      const matchedVariation = variationIds.find((variation) => variation.uniqueID === targetId);
      if (matchedVariation) {
        const variationIdSlotNumber = matchedVariation.id;
        const matchedSlot = componentToCheck.variations?.find((slot) => variationIdSlotNumber === slot.slotNumber);
        if (matchedSlot) {
          bayComponent = matchedSlot.content;
        }
      }
    }
  }
  return bayComponent;
};

const getBentoHeroImages = ({ bays, isMobile, renderDefault, variationIds }) => {
  const bentoHeroImages = [];
  bays.forEach((bay, id) => {
    const bentoHeroId = isMobile ? `preloadBentoSmallImage${id}` : `preloadBentoLargeImage${id}`;
    const firstComponent = bay?.components?.[0];
    if (!firstComponent) return;

    let bayComponent = getComponentFromComponentSelector(firstComponent, renderDefault, variationIds);

    const previewImage = isMobile ? bayComponent?.mobilePreviewImage : bayComponent?.previewImage;

    const previewImageUrl = getImageUrl(previewImage);

    const posterImageId = isMobile ? `bentoPosterImage${id}` : `bentoDesktopPosterImage${id}`;
    const posterImage = isMobile ? bayComponent?.posterImage : bayComponent?.desktopPosterImage;
    const posterImageUrl = getImageUrl(posterImage);

    // Add preview image for preloading
    if (previewImageUrl) {
      bentoHeroImages.push({
        preloadedImageUrl: previewImageUrl,
        preloadedImageId: bentoHeroId
      });
    }

    // Add poster image for preloading (for video bays)
    if (posterImageUrl) {
      bentoHeroImages.push({
        preloadedImageUrl: posterImageUrl,
        preloadedImageId: posterImageId
      });
    }
  });
  return bentoHeroImages;
};

export const getPreloadedImages = ({ layoutComponents = [], isMobile, renderDefault, variationIds }) => {
  const preloadedImages = [];
  const heroCarouselData = layoutComponents?.find((component) => component.customRenderId === 'Hero Carousel');
  const bentoHeroData = layoutComponents?.find((component) => component.customRenderId === BENTO_HERO);

  if (heroCarouselData) {
    preloadedImages.push(...getHeroImages({ heroCarouselData, isMobile }));
  }

  if (bentoHeroData) {
    const { firstBay, secondBay, thirdBay, fourthBay } = bentoHeroData;
    const bays = isMobile ? [firstBay, secondBay] : [firstBay, secondBay, thirdBay, fourthBay];
    preloadedImages.push(...getBentoHeroImages({ bays, isMobile, renderDefault, variationIds }));
  }

  return preloadedImages;
};
